import { useEffect } from 'react'

const DEFAULT_DESCRIPTION = 'Welcome to Credibila. Expertly-Crafted Investments, Accessible to All.'

type PageMeta = {
  enabled?: boolean
  title: string
  description: string
  canonicalPath: string
  image?: string
  ogType?: 'website' | 'article'
  jsonLd?: string
}

const upsertMeta = (attribute: 'name' | 'property', key: string, content: string) => {
  const selector = `meta[${attribute}="${key}"]`
  let element = document.head.querySelector(selector)
  if (!(element instanceof HTMLMetaElement)) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

export const usePageMeta = ({
  enabled = true,
  title,
  description,
  canonicalPath,
  image,
  ogType = 'website',
  jsonLd,
}: PageMeta) => {
  useEffect(() => {
    if (!enabled) return

    const previousTitle = document.title
    const descriptionMeta = document.head.querySelector('meta[name="description"]')
    const previousDescription =
      descriptionMeta instanceof HTMLMetaElement
        ? descriptionMeta.getAttribute('content') ?? DEFAULT_DESCRIPTION
        : DEFAULT_DESCRIPTION

    const pageTitle = title.trim()
    const pageDescription = description.trim()
    document.title = pageTitle
    upsertMeta('name', 'description', pageDescription)

    const canonicalHref = `${window.location.origin}${canonicalPath}`
    const existingCanonical = document.head.querySelector('link[rel="canonical"]')
    const createdCanonical = !(existingCanonical instanceof HTMLLinkElement)
    const canonicalLink: HTMLLinkElement =
      existingCanonical instanceof HTMLLinkElement
        ? existingCanonical
        : document.createElement('link')
    if (createdCanonical) {
      canonicalLink.rel = 'canonical'
      document.head.appendChild(canonicalLink)
    }
    canonicalLink.href = canonicalHref

    upsertMeta('property', 'og:title', pageTitle)
    upsertMeta('property', 'og:description', pageDescription)
    upsertMeta('property', 'og:url', canonicalHref)
    upsertMeta('property', 'og:type', ogType)
    if (image) upsertMeta('property', 'og:image', image)

    let jsonLdScript: HTMLScriptElement | null = null
    if (jsonLd) {
      jsonLdScript = document.createElement('script')
      jsonLdScript.type = 'application/ld+json'
      jsonLdScript.id = 'credibila-jsonld'
      jsonLdScript.text = jsonLd
      document.head.appendChild(jsonLdScript)
    }

    return () => {
      document.title = previousTitle
      upsertMeta('name', 'description', previousDescription)
      if (createdCanonical) canonicalLink.remove()
      jsonLdScript?.remove()
      document.head.querySelectorAll('meta[property^="og:"]').forEach((node) => node.remove())
    }
  }, [canonicalPath, description, enabled, image, jsonLd, ogType, title])
}
